/**
 * Hearth's DEMO catalog — the TISLIT range, and what a grid falls back to when nothing real resolves.
 *
 * It lives in ui-react, not in the storefront app, because the BUILDER needs it too: a seller opening
 * Hearth on an empty store would otherwise get the house fallback (a clothing shop, "Cotton Crew Tee")
 * inside a homeware theme (spec 0032 §3 always listed `catalog` as one of a theme's four pieces).
 *
 * TYPE-ONLY IMPORTS on purpose: `sections/_shared/product.ts` reads this module through
 * `themes/demoCatalogs.ts`, so anything but a type import here would close an import cycle.
 *
 * Prices carry the STORE's currency rather than one of their own — the same demo has to read correctly
 * in a MAD shop and in a EUR one (0049).
 */
const IMG = '/demo/homeware';
/**
 * Homeware is the one niche where the buy-box needs TWO real options, and where getting them wrong is
 * visible: a throw has a colourway, a bed set has a colourway AND a bed size. Naming them "Coloris" and
 * "Dimensions" is also what lets the collection page's facets point at something real instead of the
 * dead "Color"/"Size" menus the grid ships by default (playbook trap 20).
 */
function product(currency, p) {
    const options = [
        {
            name: 'Coloris',
            values: (p.colours ?? ['Avoine', 'Olive', 'Argile']).map((label) => ({ label })),
        },
    ];
    if (p.sizes)
        options.push({ name: 'Dimensions', values: p.sizes.map((label) => ({ label })) });
    return {
        id: p.id,
        name: p.name,
        slug: p.slug,
        description: p.desc,
        price: { amount: p.price, currency },
        compareAtPrice: p.compareAt ? { amount: p.compareAt, currency } : null,
        images: [{ url: `${IMG}/${p.image}`, alt: p.alt, isCover: true }],
        inventory: { tracked: false, quantity: null },
        seo: { metaTitle: null, metaDescription: null },
        status: 'active',
        tags: p.badge ? [p.badge] : [],
        vendor: 'TISLIT',
        rating: p.rating,
        options,
    };
}
/** The TISLIT range, in merchandising order. */
export function hearthDemoProducts(currency) {
    return [
        product(currency, {
            id: 'p-tislit-parure',
            name: 'Parure de lit en lin lavé',
            slug: 'parure-de-lit-lin-lave',
            price: 89000,
            compareAt: 109000,
            badge: 'custom-badge-1',
            image: 'produit-parure.webp',
            alt: 'Parure de lit en lin lavé TISLIT, coloris avoine, sur un lit défait',
            rating: { value: 4.9, count: 386 },
            desc: 'Lin lavé européen, adouci au tambour. Housse de couette et deux taies — plus souple à chaque lavage.',
            colours: ['Avoine', 'Olive', 'Argile'],
            sizes: ['160 × 200', '180 × 200', '200 × 200'],
        }),
        product(currency, {
            id: 'p-tislit-plaid',
            name: 'Plaid en laine tissé main',
            slug: 'plaid-laine-tisse-main',
            price: 49000,
            image: 'produit-plaid.webp',
            alt: 'Plaid en laine tissé main TISLIT plié sur un accoudoir',
            rating: { value: 4.8, count: 241 },
            desc: 'Tissé sur métier à bras dans le Moyen Atlas. Laine non teintée, franges nouées une à une.',
            colours: ['Avoine', 'Olive'],
        }),
        product(currency, {
            id: 'p-tislit-coussin',
            name: 'Coussin en lin brodé',
            slug: 'coussin-lin-brode',
            price: 19000,
            image: 'produit-coussin.webp',
            alt: 'Coussin en lin brodé TISLIT posé sur un canapé écru',
            rating: { value: 4.7, count: 178 },
            desc: 'Housse 45 × 45 en lin lavé, broderie au fil de coton, fermeture à boutons de bois.',
        }),
        product(currency, {
            id: 'p-tislit-tapis',
            name: 'Tapis en laine noué main',
            slug: 'tapis-laine-noue-main',
            price: 149000,
            compareAt: 179000,
            badge: 'custom-badge-2',
            image: 'produit-tapis.webp',
            alt: 'Tapis en laine noué main TISLIT au sol d’un salon clair',
            rating: { value: 4.9, count: 96 },
            desc: 'Noué main, laine vierge sur chaîne de coton. Chaque pièce est unique — les nuances varient.',
            colours: ['Avoine', 'Olive'],
            sizes: ['120 × 170', '160 × 230', '200 × 300'],
        }),
        product(currency, {
            id: 'p-tislit-vase',
            name: 'Vase en terre cuite émaillée',
            slug: 'vase-terre-cuite-emaillee',
            price: 29000,
            image: 'produit-vase.webp',
            alt: 'Vase en terre cuite émaillée verte TISLIT sur une console',
            rating: { value: 4.8, count: 203 },
            desc: 'Tourné puis émaillé au pinceau — la terre reste nue au pied. Étanche, 28 cm de haut.',
            colours: ['Olive', 'Argile'],
        }),
        product(currency, {
            id: 'p-tislit-lampe',
            name: 'Lampe à poser en rotin',
            slug: 'lampe-a-poser-rotin',
            price: 45000,
            image: 'produit-lampe.webp',
            alt: 'Lampe à poser en rotin TISLIT allumée sur une table de chevet',
            rating: { value: 4.7, count: 134 },
            desc: 'Abat-jour en rotin tressé, pied en chêne huilé. Douille E27, cordon tissé de 1,80 m.',
            colours: ['Naturel'],
        }),
        product(currency, {
            id: 'p-tislit-bougie',
            name: 'Bougie parfumée cèdre & figue',
            slug: 'bougie-parfumee-cedre-figue',
            price: 14000,
            image: 'produit-bougie.webp',
            alt: 'Bougie TISLIT en verre ambré, mèche allumée, sur une pile de livres',
            rating: { value: 4.8, count: 312 },
            desc: 'Cire végétale, mèche en coton, 45 heures. Cèdre de l’Atlas, figue et un fond de miel.',
            colours: ['Ambre'],
        }),
        product(currency, {
            id: 'p-tislit-bols',
            name: 'Service de quatre bols émaillés',
            slug: 'service-quatre-bols-emailles',
            price: 34000,
            image: 'produit-bols.webp',
            alt: 'Quatre bols en céramique émaillée TISLIT empilés sur un plan de travail',
            rating: { value: 4.8, count: 167 },
            desc: 'Quatre bols tournés, émail réactif — deux ne sont jamais tout à fait identiques. Lave-vaisselle.',
            colours: ['Olive', 'Argile'],
        }),
        product(currency, {
            id: 'p-tislit-plateau',
            name: 'Plateau en chêne massif',
            slug: 'plateau-chene-massif',
            price: 26000,
            image: 'produit-plateau.webp',
            alt: 'Plateau en chêne massif TISLIT avec une théière et deux verres',
            rating: { value: 4.7, count: 121 },
            desc: 'Chêne massif huilé, poignées creusées dans la masse. 45 × 30 cm, se nourrit à l’huile de lin.',
            colours: ['Naturel'],
        }),
        product(currency, {
            id: 'p-tislit-panier',
            name: 'Panier tressé en doum',
            slug: 'panier-tresse-doum',
            price: 22000,
            image: 'produit-panier.webp',
            alt: 'Panier tressé en doum TISLIT dans une entrée, rempli de plaids',
            rating: { value: 4.6, count: 88 },
            desc: 'Doum tressé à la main, anses en cuir végétal. Pour le bois, les plaids ou le linge.',
            colours: ['Naturel'],
            sizes: ['Moyen', 'Grand'],
        }),
    ];
}
/**
 * Hearth merchandises by ROOM, not by material — c'est ainsi qu'on achète pour sa maison : personne
 * n'entre en cherchant « grès émaillé », on entre parce que le salon sonne vide.
 *
 * QUATRE collections, pas cinq : `collection-list` plafonne à 4 colonnes, et une cinquième tuile
 * laisserait une rangée bancale (piège n°3 du mode d'emploi, la colonne fantôme).
 */
export function hearthDemoCollections() {
    const make = (id, name, slug, cover, alt, productCount, description) => ({
        id,
        name,
        slug,
        description,
        coverImage: { url: `${IMG}/${cover}`, alt },
        productCount,
    });
    return [
        make('c-salon', 'Salon', 'salon', 'piece-salon.webp', 'Coin de salon TISLIT : plaid en laine sur un canapé écru, tapis et vase', 6, 'Là où on s’assoit vraiment. Plaids, coussins, tapis et ce qui fait qu’on reste.'),
        make('c-chambre', 'Chambre', 'chambre', 'piece-chambre.webp', 'Lit habillé de lin lavé TISLIT, lampe en rotin sur la table de chevet', 5, 'Du lin qui s’adoucit et une lumière qu’on baisse. Le reste peut attendre demain.'),
        make('c-cuisine', 'Cuisine', 'cuisine', 'piece-cuisine.webp', 'Plan de travail TISLIT : bols émaillés empilés et plateau en chêne', 4, 'La pièce où l’on parle. Terre cuite, chêne et rien qui craigne d’être utilisé.'),
        make('c-entree', 'Entrée', 'entree', 'piece-entree.webp', 'Entrée TISLIT : panier en doum, miroir et patère de bois', 3, 'Les trois mètres carrés que tout le monde traverse et que personne ne regarde.'),
    ];
}
//# sourceMappingURL=catalog.js.map